import {
  API_ROUTES,
  type CreateUploadSessionRequest,
  uploadSessionTicketSchema,
} from '@hvac/contracts';
import { z } from 'zod';
import { apiClient } from '../api/queries.js';
import { supabase } from '../auth/supabase.js';
import type { UploadTransport } from './upload-manager.js';

/**
 * The real transport.
 *
 * Two channels: JSON to our API for session lifecycle, and raw bytes straight
 * to Supabase Storage. The API never sees a byte of a document.
 */

export function createUploadTransport(organizationId: string): UploadTransport {
  return {
    async createSession(request: CreateUploadSessionRequest) {
      return apiClient.request({
        method: 'POST',
        path: API_ROUTES.intake.createSession(organizationId),
        schema: uploadSessionTicketSchema,
        body: request,
      });
    },

    /**
     * `XMLHttpRequest`, not `fetch`.
     *
     * `fetch` still cannot report upload progress in any shipping browser,
     * request streaming is HTTP/2-only, Chromium-only, and requires a duplex
     * half nothing else implements. For a screen whose whole job is showing a
     * hundred progress bars, XHR is the correct tool, not a legacy one.
     */
    putBytes({ uploadUrl, file, mimeType, signal, onProgress }) {
      return new Promise<void>((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('PUT', uploadUrl, true);
        request.setRequestHeader('content-type', mimeType);
        // Storage keys are unique per session, so a collision means something
        // is badly wrong and should fail loudly rather than overwrite.
        request.setRequestHeader('x-upsert', 'false');

        request.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress(event.loaded / event.total);
        });

        request.addEventListener('load', () => {
          if (request.status >= 200 && request.status < 300) {
            resolve();
            return;
          }
          reject(
            Object.assign(new Error(`Storage rejected the upload (${request.status}).`), {
              httpStatus: request.status,
            }),
          );
        });

        request.addEventListener('error', () =>
          reject(new Error('The connection dropped during upload.')),
        );
        request.addEventListener('timeout', () => reject(new Error('The upload timed out.')));

        const abort = (): void => request.abort();
        signal.addEventListener('abort', abort, { once: true });
        request.addEventListener('abort', () =>
          reject(new DOMException('Upload cancelled.', 'AbortError')),
        );
        request.addEventListener('loadend', () => signal.removeEventListener('abort', abort));

        request.send(file);
      });
    },

    /**
     * Previews go straight to their own bucket with a client-chosen key.
     *
     * Safe because the storage policy enforces the organisation prefix: a key
     * outside the caller's tenant is rejected by the database, not by our
     * politeness. Server-minting a key here would add a round trip per file to
     * a hundred-file upload for no gain.
     */
    async uploadPreview({ organizationId: orgId, blob }) {
      const objectKey = `${orgId}/previews/${crypto.randomUUID()}.webp`;
      const { error } = await supabase.storage
        .from('document-previews')
        .upload(objectKey, blob, { contentType: 'image/webp', upsert: false });

      if (error !== null) return null;
      return { objectKey, byteSize: blob.size };
    },

    async completeSession(sessionId, body) {
      return apiClient.request({
        method: 'POST',
        path: API_ROUTES.intake.completeSession(organizationId, sessionId),
        schema: z.object({ documentId: z.uuid() }),
        body,
      });
    },

    async abandonSession(sessionId, reason) {
      await apiClient.request({
        method: 'POST',
        path: API_ROUTES.intake.abandonSession(organizationId, sessionId),
        schema: z.undefined(),
        body: { reason },
      });
    },
  };
}
